import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  Map,
  useMap,
  APIProvider
} from '@vis.gl/react-google-maps';
import { Alert } from 'react-bootstrap';
import CustomInfoWindow from './custom-window-info';
import MapLegendConfig from './map-legend-config';
import config from '../../config';
import { useTrip } from '../contexts/trip-context';
import ActivityTypeHelper, { ActivityType } from '../../types/activity-types';
import { renderToStaticMarkup } from 'react-dom/server';
import ItinerariesSvg from '../itineraries/itineraries-svg';
import useMapBounds from '../map/use-map-bounds';
import tripActivityService from '../../services/trips/trip-activity.service';
import { Plus } from 'lucide-react';
import { ISummaryItem } from '../../services/summaries/summary.service';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import ActivityAppearanceService from '../../services/activity-appearance.service';

// Constants
const ZOOM_LIMITS = {
  MIN: 1,
  MAX_AFTER_FIT: 14,
  DEFAULT: 10
} as const;

const MARKER_SIZES = {
  NORMAL: 28,
  LARGE: 40,
  XLARGE: 52
} as const;

export type MapWithMarkersProps = {
  tripId: string;
  folderId?: string;
  markers: MarkerInfo[];
  boundsMarkers?: MarkerInfo[];
  recenterKey?: number;
  aiSummaries?: ISummaryItem[];
  showSearchResultMarkers?: boolean;
  onViewportChange?: (viewport: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => void;
  lockBounds?: boolean;
  selectedActivityIds?: string[]; // Phase 2: Accept as prop instead of context
};

export type MarkerInfo = {
  id?: string;
  tripId?: string;
  folders?: string[];
  placesId?: string;
  lat: number;
  lng: number;
  type?: string;
  types?: string[]; // Google Places types array
  name: string;
  notes?: string[];
  websiteUrl?: string;
  index?: number; // For AI result numbering

  // Additional Google Places data
  formattedAddress?: string;
  businessStatus?: string;
  rating?: number;
  userRatingsTotal?: number;
  priceLevel?: number;
  photos?: google.maps.places.PlacePhoto[];
  openingHours?: google.maps.places.PlaceOpeningHours;
  icon?: string;
  iconBackgroundColor?: string;
  iconMaskBaseUri?: string;
  plusCode?: google.maps.places.PlacePlusCode;
  formattedPhoneNumber?: string;
  internationalPhoneNumber?: string;
  editorialSummary?: string;
  thumbnail?: string; // Backend photo URL (from serpapiThumbnail or thumbnail)
};

const MapWithMarkers: React.FC<MapWithMarkersProps> = ({
  tripId,
  folderId,
  markers,
  boundsMarkers,
  recenterKey,
  aiSummaries = [],
  showSearchResultMarkers = true,
  onViewportChange,
  lockBounds = false,
  selectedActivityIds: propSelectedActivityIds
}) => {
  const [selectedMarker, setSelectedMarker] = useState<MarkerInfo | null>(null);
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showLegendConfig, setShowLegendConfig] = useState(false);
  const [legendPosition, setLegendPosition] = useState({ x: 0, y: 0 });
  const [configVersion, setConfigVersion] = useState(0);
  const [previewCustomization, setPreviewCustomization] = useState<{
    activityId: string;
    color: string;
    icon: string;
  } | null>(null);

  const mapInstance = useMap();
  const { selectedFolderId, hoveredActivity, trip, addActivity } = useTrip();

  // Phase 2: Use prop if provided, otherwise fall back to empty array
  const selectedActivityIds = useMemo(
    () => propSelectedActivityIds ?? [],
    [propSelectedActivityIds]
  );

  // Handler for adding a search result to the trip
  const handleAddSearchResult = async (marker: MarkerInfo) => {
    if (isAddingActivity) return;

    setIsAddingActivity(true);
    setError(null);
    setSuccess(null);

    try {
      // Prepare activity data from search result marker
      const activityData = {
        activityId: '', // Backend will assign the real ID
        title: marker.name,
        description: marker.notes?.[0] || '',
        websiteUrl: marker.websiteUrl || undefined,
        isPublic: false,
        imageUrl: '', // Let backend handle default
        folderId: folderId || null,
        tripId: tripId,
        type: undefined,
        types: [],
        tags: [],
        placesId: marker.placesId || undefined,
        latitude: marker.lat,
        longitude: marker.lng,
        addressComponents: []
      };

      // Create the activity directly
      // console.log(
      //   '[MapWithMarkers] Creating activity with data:',
      //   activityData
      // );
      const result = await tripActivityService.createActivity(
        tripId,
        activityData
      );
      // console.log('[MapWithMarkers] Activity created, result:', result);
      // console.log('[MapWithMarkers] Result.activity:', result?.activity);

      if (result && result.activity) {
        // console.log(
        //   '[MapWithMarkers] Adding activity to context:',
        //   result.activity
        // );
        addActivity(result.activity);

        // Remove the added marker from its source list so it shows only as a trip-activity marker
        if (marker.type === 'search_result') {
          // Search result marker will be filtered out by parent component
          // console.log(
          //   '[MapWithMarkers] Search result added, parent will handle cleanup'
          // );
        } else if (marker.type === 'ai_result') {
          // AI result marker will be filtered out by parent component (TripContentMap)
          // console.log(
          //   '[MapWithMarkers] AI result added, parent will handle cleanup'
          // );
        }

        setSuccess(`✅ "${marker.name}" added to your trip`);
        setSelectedMarker(null);
        setPreviewCustomization(null); // Clear any preview

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      } else {
        console.error('[MapWithMarkers] Invalid response structure:', {
          hasResult: !!result,
          hasActivity: !!result?.activity
        });
        setError('Failed to add activity - invalid response');
      }
    } catch (error: any) {
      console.error('[MapWithMarkers] Error adding search result:', error);
      console.error('[MapWithMarkers] Error details:', {
        message: error?.message,
        status: error?.status,
        error: error?.error,
        stack: error?.stack
      });

      let errorMessage = 'Failed to add activity. Please try again.';

      // Extract the most specific error message
      if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (
        error?.message &&
        error.message !== 'Failed to add trip activity to trip'
      ) {
        errorMessage = error.message;
      }

      // Special handling for duplicate error
      if (errorMessage.includes('already exists')) {
        errorMessage = `This place is already in your trip. ${errorMessage.split('.')[0]}.`;
      }

      setError(errorMessage);
    } finally {
      setIsAddingActivity(false);
    }
  };

  // Track previous bounds to detect significant changes
  const previousBoundsRef = React.useRef<{
    north: number;
    south: number;
    east: number;
    west: number;
  } | null>(null);

  // Track previous visibleMarkersKey to detect real changes
  const prevVisibleMarkersKeyRef = React.useRef<string>('');

  // Track previous recenter key so we only recenter on actual changes.
  const prevRecenterKeyRef = React.useRef<number | undefined>(undefined);

  // Track initial load to prevent zoom changes after user interaction
  const initialLoadRef = React.useRef<boolean>(true);

  // Reset state on unmount so a future mount always gets a fresh fit
  useEffect(() => {
    return () => {
      initialLoadRef.current = true;
      previousBoundsRef.current = null;
    };
  }, []);

  // Filter markers based on activities array and folder selection
  const visibleMarkers = useMemo(() => {
    // aiResultMarkers processing removed - now passed via markers prop

    const allMarkers = [
      ...markers
      // Note: search results and AI results are now passed via markers prop
    ];

    const filtered = allMarkers.filter((marker) => {
      // Always show search results and AI results
      if (marker.type === 'search_result' || marker.type === 'ai_result') {
        return showSearchResultMarkers;
      }

      // Hide destination markers when folder is selected or activities are checked
      if (marker.type === 'destination') {
        return !(selectedFolderId || selectedActivityIds.length > 0);
      }

      // Only show activity markers that are selected (checked)
      if (marker.id) {
        return selectedActivityIds.includes(marker.id.toString());
      }

      return false;
    });

    // console.log('[MapWithMarkers] Marker filtering:', {
    //   totalMarkers: allMarkers.length,
    //   visibleMarkers: filtered.length,
    //   searchResults: allMarkers.filter((m) => m.type === 'search_result')
    //     .length,
    //   visibleSearchResults: filtered.filter((m) => m.type === 'search_result')
    //     .length,
    //   showSearchResultMarkers,
    //   searchResultSample: filtered
    //     .filter((m) => m.type === 'search_result')
    //     .slice(0, 3)
    //     .map((m) => ({ name: m.name, lat: m.lat, lng: m.lng }))
    // });

    return filtered;
  }, [markers, showSearchResultMarkers, selectedFolderId, selectedActivityIds]);

  // Create a stable key for visibleMarkers to prevent unnecessary effect triggers
  const visibleMarkersKey = useMemo(
    () =>
      JSON.stringify(
        visibleMarkers.map((m) => ({
          id: m.id,
          lat: m.lat,
          lng: m.lng,
          type: m.type
        }))
      ),
    [visibleMarkers]
  );

  // Bounds can be driven by a different marker set than what's visible.
  const viewportMarkers = useMemo(() => {
    if (boundsMarkers && boundsMarkers.length > 0) {
      return boundsMarkers;
    }
    return visibleMarkers;
  }, [boundsMarkers, visibleMarkers]);

  // Use the hook to calculate center and zoom from default viewport markers.
  const {
    centerPosition,
    zoom: calculatedZoom,
    bounds
  } = useMapBounds(viewportMarkers);

  // Helper function to check if bounds changed significantly (>10%)
  const boundsChangedSignificantly = useCallback(
    (
      oldBounds: {
        north: number;
        south: number;
        east: number;
        west: number;
      } | null,
      newBounds: {
        north: number;
        south: number;
        east: number;
        west: number;
      } | null
    ): boolean => {
      if (!oldBounds || !newBounds) return true;

      const oldLatSpan = oldBounds.north - oldBounds.south;
      const oldLngSpan = oldBounds.east - oldBounds.west;
      const newLatSpan = newBounds.north - newBounds.south;
      const newLngSpan = newBounds.east - newBounds.west;

      const latChange = Math.abs(newLatSpan - oldLatSpan) / oldLatSpan;
      const lngChange = Math.abs(newLngSpan - oldLngSpan) / oldLngSpan;

      const maxChange = Math.max(latChange, lngChange);

      return maxChange > 0.1; // More than 10% change
    },
    []
  );

  // Helper function to check if all markers are visible in current map bounds
  const allMarkersVisible = useCallback(
    (markers: MarkerInfo[]): boolean => {
      if (!mapInstance) return false;

      const currentBounds = mapInstance.getBounds();
      if (!currentBounds) return false;

      const allVisible = markers.every((marker) => {
        const markerLatLng = new google.maps.LatLng(marker.lat, marker.lng);
        return currentBounds.contains(markerLatLng);
      });

      return allVisible;
    },
    [mapInstance]
  );

  // Expand viewport only when newly visible markers fall outside current bounds.
  // This avoids map movement on hide/show unless expansion is required.
  const expandViewportIfNeeded = useCallback(
    (markers: MarkerInfo[]): boolean => {
      if (!mapInstance || !window.google?.maps?.LatLngBounds) return false;

      const currentBounds = mapInstance.getBounds();
      if (!currentBounds || markers.length === 0) return false;

      const expandedBounds = new google.maps.LatLngBounds(
        currentBounds.getSouthWest(),
        currentBounds.getNorthEast()
      );

      let requiresExpand = false;

      markers.forEach((marker) => {
        const latLng = new google.maps.LatLng(marker.lat, marker.lng);
        if (!currentBounds.contains(latLng)) {
          requiresExpand = true;
          expandedBounds.extend(latLng);
        }
      });

      if (!requiresExpand) return false;

      mapInstance.fitBounds(expandedBounds);

      // Keep a reasonable max zoom after expand fit.
      setTimeout(() => {
        const currentZoom = mapInstance.getZoom() || ZOOM_LIMITS.DEFAULT;
        if (currentZoom > ZOOM_LIMITS.MAX_AFTER_FIT) {
          mapInstance.setZoom(ZOOM_LIMITS.MAX_AFTER_FIT);
        }
      }, 100);

      return true;
    },
    [mapInstance]
  );

  // Update map using fitBounds (new approach) or fallback to center+zoom (original logic)
  useEffect(() => {
    const markersChanged =
      prevVisibleMarkersKeyRef.current !== visibleMarkersKey &&
      prevVisibleMarkersKeyRef.current !== '';

    if (!mapInstance) return;

    // console.log('[MapWithMarkers] Map update effect triggered', {
    //   isInitialLoad: initialLoadRef.current,
    //   visibleMarkers: visibleMarkers.length,
    //   viewportMarkers: viewportMarkers.length,
    //   hasBounds: !!bounds,
    //   markersChanged
    // });

    // When boundsMarkers controls defaults, do not refit on each marker toggle.
    // Only expand if the DEFAULT viewport marker set is outside current bounds.
    // This keeps day/item checkbox toggles from changing zoom unexpectedly.
    if (!initialLoadRef.current && boundsMarkers && markersChanged) {
      prevVisibleMarkersKeyRef.current = visibleMarkersKey;
      // const expanded = expandViewportIfNeeded(viewportMarkers);
      // if (expanded) {
      //   console.log(
      //     '[MapWithMarkers] Expanded viewport to include default bounds markers'
      //   );
      // } else {
      //   console.log(
      //     '[MapWithMarkers] Marker toggle within default viewport - no bounds update'
      //   );
      // }
      return;
    }

    // Determine if we should update the map:
    // - Always update on initial load
    // - When boundsMarkers is provided, suppress marker-change refits so clicking
    //   checkboxes does not move the map.
    // - When boundsMarkers is not provided, retain marker-change behavior for other views.
    const shouldUpdateMap =
      initialLoadRef.current || (!boundsMarkers && markersChanged);

    if (!shouldUpdateMap) {
      console.log(
        '[MapWithMarkers] Skipping - not initial load and markers unchanged'
      );
      return;
    }

    // Track that markers have been processed
    prevVisibleMarkersKeyRef.current = visibleMarkersKey;

    // Check if we should skip the bounds update based on visibility
    const shouldSkipUpdate =
      !initialLoadRef.current && // Only check visibility if not initial load
      bounds &&
      previousBoundsRef.current &&
      !boundsChangedSignificantly(previousBoundsRef.current, bounds) &&
      allMarkersVisible(visibleMarkers);

    if (shouldSkipUpdate) {
      console.log(
        '[MapWithMarkers] Skipping - bounds change < 10% and all markers visible'
      );
      return;
    }

    console.log('[MapWithMarkers] Updating map bounds/zoom');

    // Special case: single destination marker → fit to full place boundaries via Places API
    const isSingleDestination =
      viewportMarkers.length === 1 && viewportMarkers[0].type === 'destination';

    if (isSingleDestination) {
      const destMarker = viewportMarkers[0];
      const placeId = destMarker.placesId;

      const finishLoad = () => {
        previousBoundsRef.current = bounds;
        setTimeout(() => {
          initialLoadRef.current = false;
        }, 100);
      };

      if (placeId && window.google?.maps?.places?.PlacesService) {
        const svc = new google.maps.places.PlacesService(mapInstance);
        svc.getDetails({ placeId, fields: ['geometry'] }, (result, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            result?.geometry?.viewport
          ) {
            mapInstance.fitBounds(result.geometry.viewport);
          } else {
            // Fallback: center on the marker with a city-level zoom
            mapInstance.setCenter({ lat: destMarker.lat, lng: destMarker.lng });
            mapInstance.setZoom(12);
          }
          finishLoad();
        });
        return; // will finish asynchronously in the callback
      } else {
        // No placeId – center on the marker with a city-level zoom
        mapInstance.setCenter({ lat: destMarker.lat, lng: destMarker.lng });
        mapInstance.setZoom(12);
        finishLoad();
        return;
      }
    }

    // New approach: Use fitBounds with padded bounds for accurate 10% margin
    if (bounds && window.google?.maps?.LatLngBounds) {
      const googleBounds = new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      );

      mapInstance.fitBounds(googleBounds);
      // console.log('[MapWithMarkers] Applied fitBounds');

      // Store the new bounds for next comparison
      previousBoundsRef.current = bounds;

      // Limit max zoom after fitBounds
      setTimeout(() => {
        const currentZoom = mapInstance.getZoom() || 10;
        if (currentZoom > ZOOM_LIMITS.MAX_AFTER_FIT) {
          mapInstance.setZoom(ZOOM_LIMITS.MAX_AFTER_FIT);
        }
        // Mark initial load as complete after zoom is set
        initialLoadRef.current = false;
        // console.log('[MapWithMarkers] Initial load complete');
      }, 100);
    } else {
      // Fallback: Original logic using center + zoom
      if (centerPosition.lat !== 0 || centerPosition.lng !== 0) {
        mapInstance.setCenter(centerPosition);
      }
      mapInstance.setZoom(calculatedZoom);

      // Mark initial load as complete
      initialLoadRef.current = false;
    }
  }, [
    mapInstance,
    bounds,
    centerPosition,
    calculatedZoom,
    visibleMarkersKey,
    boundsChangedSignificantly,
    allMarkersVisible,
    expandViewportIfNeeded,
    visibleMarkers,
    viewportMarkers,
    boundsMarkers
  ]);

  // Listen for viewport changes and notify parent
  useEffect(() => {
    if (!mapInstance || !onViewportChange) return;

    const listener = mapInstance.addListener('bounds_changed', () => {
      const bounds = mapInstance.getBounds();
      if (bounds) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        onViewportChange({
          north: ne.lat(),
          south: sw.lat(),
          east: ne.lng(),
          west: sw.lng()
        });
      }
    });

    return () => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
    };
  }, [mapInstance, onViewportChange]);

  const handleLegendClose = useCallback(() => {
    setShowLegendConfig(false);
    // Increment version to trigger marker re-render with new colors
    setConfigVersion((prev) => prev + 1);
  }, []);

  // Handle customization save - refresh markers
  const handleCustomizationSave = useCallback(() => {
    // Clear preview
    setPreviewCustomization(null);
    // Increment version to trigger marker re-render with saved colors
    setConfigVersion((prev) => prev + 1);
  }, []);

  // Handle customization preview - update immediately without save
  const handleCustomizationPreview = useCallback(
    (color: string, icon: string) => {
      if (selectedMarker?.id) {
        setPreviewCustomization({
          activityId: selectedMarker.id,
          color,
          icon
        });
      }
    },
    [selectedMarker]
  );

  // Clear preview when info window is closed
  const handleMarkerClick = useCallback((marker: MarkerInfo) => {
    setSelectedMarker(marker);
    setPreviewCustomization(null); // Clear any preview from previous selection
  }, []);

  // Handle closing the info window - clear preview to revert unsaved changes
  const handleCloseInfoWindow = useCallback(() => {
    setSelectedMarker(null);
    setPreviewCustomization(null); // Revert any unsaved preview changes
  }, []);

  // Handle recenter - fit all visible markers in view
  const handleRecenter = useCallback(() => {
    if (!mapInstance || !bounds) return;

    // Special case: single destination marker
    const isSingleDestination =
      viewportMarkers.length === 1 && viewportMarkers[0].type === 'destination';

    if (isSingleDestination) {
      const destMarker = viewportMarkers[0];
      const placeId = destMarker.placesId;

      if (placeId && window.google?.maps?.places?.PlacesService) {
        const svc = new google.maps.places.PlacesService(mapInstance);
        svc.getDetails({ placeId, fields: ['geometry'] }, (result, status) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            result?.geometry?.viewport
          ) {
            mapInstance.fitBounds(result.geometry.viewport);
          } else {
            mapInstance.setCenter({ lat: destMarker.lat, lng: destMarker.lng });
            mapInstance.setZoom(12);
          }
        });
        return;
      } else {
        mapInstance.setCenter({ lat: destMarker.lat, lng: destMarker.lng });
        mapInstance.setZoom(12);
        return;
      }
    }

    // Use fitBounds for multiple markers
    if (window.google?.maps?.LatLngBounds) {
      const googleBounds = new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      );

      mapInstance.fitBounds(googleBounds);

      // Limit max zoom after fitBounds
      setTimeout(() => {
        const currentZoom = mapInstance.getZoom() || 10;
        if (currentZoom > ZOOM_LIMITS.MAX_AFTER_FIT) {
          mapInstance.setZoom(ZOOM_LIMITS.MAX_AFTER_FIT);
        }
      }, 100);
    }
  }, [mapInstance, bounds, viewportMarkers]);

  // Programmatic recenter trigger used by parent views to reset to default bounds.
  useEffect(() => {
    if (recenterKey === undefined) return;

    if (prevRecenterKeyRef.current === undefined) {
      prevRecenterKeyRef.current = recenterKey;
      return;
    }

    if (prevRecenterKeyRef.current !== recenterKey) {
      prevRecenterKeyRef.current = recenterKey;
      handleRecenter();
    }
  }, [recenterKey, handleRecenter]);

  // Add right-click event listener to map
  useEffect(() => {
    if (!mapInstance) return;

    const listener = mapInstance.addListener(
      'rightclick',
      (e: google.maps.MapMouseEvent) => {
        if (e.domEvent) {
          e.domEvent.preventDefault();
          e.domEvent.stopPropagation();
          setLegendPosition({
            x: (e.domEvent as MouseEvent).clientX,
            y: (e.domEvent as MouseEvent).clientY
          });
          setShowLegendConfig(true);
        }
      }
    );

    // Suppress default context menu on the map element and all child elements
    const mapDiv = mapInstance.getDiv();
    const contextMenuHandler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    if (mapDiv) {
      // Add listener with capture phase to intercept before it bubbles
      mapDiv.addEventListener('contextmenu', contextMenuHandler, true);

      // Also add to all child elements
      const allElements = mapDiv.querySelectorAll('*');
      allElements.forEach((el) => {
        el.addEventListener('contextmenu', contextMenuHandler, true);
      });
    }

    return () => {
      if (listener) {
        google.maps.event.removeListener(listener);
      }
      if (mapDiv) {
        mapDiv.removeEventListener('contextmenu', contextMenuHandler, true);
        const allElements = mapDiv.querySelectorAll('*');
        allElements.forEach((el) => {
          el.removeEventListener('contextmenu', contextMenuHandler, true);
        });
      }
    };
  }, [mapInstance]);

  // Get saved color for activity type from sessionStorage
  const getSavedColor = useCallback((type: ActivityType): string => {
    try {
      const saved = sessionStorage.getItem('mapLegendConfigs');
      if (saved) {
        const configs = JSON.parse(saved);
        return configs[type]?.color || '#842234';
      }
    } catch {
      // Fall through to default
    }
    return '#842234';
  }, []);

  // Create SVG icon for markers using ItinerariesSvg component
  const createSvgIcon = useCallback(
    (
      type: ActivityType,
      isSelected: boolean = false,
      isHovered: boolean = false,
      isSearchResult: boolean = false,
      isAiResult: boolean = false,
      aiIndex?: number,
      activity?: ITripActivity | MarkerInfo,
      preview?: { color: string; icon: string } | null
    ): string => {
      // Make hovered markers slightly larger than normal but smaller than selected
      const size =
        isSearchResult || isAiResult
          ? MARKER_SIZES.XLARGE
          : isHovered && !isSelected
            ? MARKER_SIZES.LARGE
            : isSelected
              ? MARKER_SIZES.LARGE
              : MARKER_SIZES.NORMAL;

      // Special handling for AI result markers with heart icon and number
      if (isAiResult) {
        // Use raw SVG heart path to ensure pink color (#EC4899)
        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <path d="M${size / 2} ${size * 0.85}c-1.5-1.5-${size * 0.35}-${size * 0.35}-${size * 0.35}-${size * 0.6}c0-${size * 0.25} ${size * 0.15}-${size * 0.45} ${size * 0.35}-${size * 0.45}s${size * 0.35} ${size * 0.2} ${size * 0.35} ${size * 0.45}c0 ${size * 0.25}-${size * 0.2} ${size * 0.45}-${size * 0.35} ${size * 0.6}z" 
                  fill="#EC4899" 
                  stroke="#EC4899" 
                  stroke-width="1.5"/>
            ${aiIndex ? `<text x="${size / 2}" y="${size / 2 + 4}" font-size="14" font-weight="bold" text-anchor="middle" fill="white">${aiIndex}</text>` : ''}
          </svg>
        `;
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      }

      // Special handling for search result markers with numbered circle
      if (isSearchResult && aiIndex) {
        // Use green circle with white number
        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" 
                    fill="#10B981" 
                    stroke="white" 
                    stroke-width="2"/>
            <text x="${size / 2}" y="${size / 2 + 5}" font-size="16" font-weight="bold" text-anchor="middle" fill="white">${aiIndex}</text>
          </svg>
        `;
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      }

      // Use ActivityAppearanceService for individual activities with customization
      let symbol: ActivityType;
      let fillColor: string;

      // Check for preview first (temporary changes)
      if (preview) {
        symbol = preview.icon as ActivityType;
        fillColor = isSearchResult ? '#10B981' : preview.color;
      } else if (
        activity &&
        'customization' in activity &&
        activity.customization
      ) {
        // Individual activity with custom appearance (saved)
        symbol = ActivityAppearanceService.getIcon(activity) as ActivityType;
        fillColor = isSearchResult
          ? '#10B981'
          : ActivityAppearanceService.getColor(activity);
      } else {
        // Fall back to type-based appearance (sessionStorage or defaults)
        symbol = ActivityTypeHelper.getSymbol(type) as ActivityType;
        fillColor = isSearchResult ? '#10B981' : getSavedColor(type);
      }

      // Different stroke for search results
      const strokeColor = isSearchResult
        ? activity && 'customization' in activity && activity.customization
          ? ActivityAppearanceService.getColor(activity)
          : getSavedColor(type)
        : 'none';
      const strokeWidth = isSearchResult ? 2 : 0;

      // Render the ItinerariesSvg component to static markup
      const iconSvg = renderToStaticMarkup(
        <ItinerariesSvg
          type={symbol as ActivityType}
          style={{ width: '100%', height: '100%' }}
          fill={fillColor}
        />
      );

      // Wrap the icon SVG in a container SVG with proper sizing and optional stroke
      const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <g transform="translate(${size * 0.15}, ${size * 0.15}) scale(${size / 30})" ${isSearchResult ? `stroke="${strokeColor}" stroke-width="${strokeWidth}"` : ''}>
          ${iconSvg.replace(/<svg[^>]*>/, '').replace('</svg>', '')}
        </g>
      </svg>
    `;

      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    },
    [getSavedColor]
  );

  // Build marker visual data for AdvancedMarker content
  const getMarkerVisual = useCallback(
    (
      typeOrActivity?: string | ITripActivity | MarkerInfo,
      isSelected: boolean = false,
      isHovered: boolean = false,
      isSearchResult: boolean = false,
      isAiResult: boolean = false,
      aiIndex?: number,
      preview?: { color: string; icon: string } | null
    ) => {
      const size =
        isSearchResult || isAiResult
          ? MARKER_SIZES.XLARGE
          : isHovered && !isSelected
            ? MARKER_SIZES.LARGE
            : isSelected
              ? MARKER_SIZES.LARGE
              : MARKER_SIZES.NORMAL;

      // Extract type and activity object
      let type: ActivityType;
      let activity: ITripActivity | MarkerInfo | undefined;

      if (typeof typeOrActivity === 'string') {
        type = ActivityTypeHelper.normalizeType(typeOrActivity);
        activity = undefined;
      } else if (typeOrActivity) {
        // Activity object provided
        activity = typeOrActivity;
        type = ActivityTypeHelper.normalizeType(typeOrActivity.type);
      } else {
        type = ActivityType.OTHER;
        activity = undefined;
      }

      const url = createSvgIcon(
        type,
        isSelected,
        isHovered,
        isSearchResult,
        isAiResult,
        aiIndex,
        activity,
        preview
      );

      return {
        url,
        size
      };
    },
    [createSvgIcon]
  );

  // handleSearch function removed - parent component now handles Google Places search

  const memoizedMarkers = useMemo(() => {
    // Don't render markers if Google Maps API is not available
    if (!window.google || !window.google.maps) {
      return [];
    }

    // Use the already-filtered visible markers
    return visibleMarkers.map((marker, index) => {
      const isHovered = hoveredActivity === marker.id?.toString();
      const isSearchResult = marker.type === 'search_result';
      const isAiResult = marker.type === 'ai_result';

      // Find full activity object if available for customization support
      const fullActivity =
        marker.id && trip?.activities
          ? trip.activities.find((a: ITripActivity) => a.id === marker.id)
          : undefined;

      // Check if this marker has preview customization
      const preview =
        previewCustomization && previewCustomization.activityId === marker.id
          ? previewCustomization
          : null;

      return (
        <AdvancedMarker
          key={index}
          position={{ lat: marker.lat, lng: marker.lng }}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
          onClick={() => handleMarkerClick(marker)}
        >
          {(() => {
            const markerVisual = getMarkerVisual(
              fullActivity || marker, // Pass full activity for customization, or marker as fallback
              false,
              isHovered,
              isSearchResult,
              isAiResult,
              marker.index,
              preview
            );

            return (
              <div
                title={marker.name}
                style={{
                  width: `${markerVisual.size}px`,
                  height: `${markerVisual.size}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={markerVisual.url}
                  alt={marker.name}
                  width={markerVisual.size}
                  height={markerVisual.size}
                  draggable={false}
                  style={{ userSelect: 'none' }}
                />
              </div>
            );
          })()}
        </AdvancedMarker>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibleMarkers,
    hoveredActivity,
    trip,
    handleMarkerClick,
    getMarkerVisual,
    configVersion,
    previewCustomization
  ]);

  // Note: handleSearch and related useEffects removed
  // Parent component now handles Google Places search and passes results via markers prop

  return (
    <div
      style={{ position: 'relative', height: '100%' }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }}
    >
      {/* Toast-style notifications */}
      {(error || success) && (
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3000,
            width: 'auto',
            maxWidth: '500px',
            minWidth: '300px'
          }}
        >
          {error && (
            <Alert
              variant='danger'
              dismissible
              onClose={() => setError(null)}
              style={{
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                margin: 0
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              variant='success'
              dismissible
              onClose={() => setSuccess(null)}
              style={{
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                margin: 0
              }}
            >
              {success}
            </Alert>
          )}
        </div>
      )}

      <Map
        style={{ width: '100%', height: '100%' }}
        defaultCenter={{ lat: 40.7128, lng: -74.006 }}
        defaultZoom={ZOOM_LIMITS.DEFAULT}
        mapId={config.GOOGLE_MAP_ID || 'DEMO_MAP_ID'}
        disableDefaultUI={true}
        fullscreenControl={false}
        zoomControl={false}
        mapTypeControl={false}
        cameraControl={false}
        gestureHandling={'greedy'}
        clickableIcons={true}
        scrollwheel={true}
        streetViewControl={false}
      >
        {memoizedMarkers}
      </Map>

      {/* Map Controls Group */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          border: '2px solid rgba(0, 0, 0, 0.2)',
          borderRadius: '6px',
          overflow: 'hidden',
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
          backgroundColor: 'white'
        }}
      >
        {/* Legend Button */}
        <button
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setLegendPosition({
              x: rect.left,
              y: rect.bottom + 10
            });
            setShowLegendConfig(true);
          }}
          style={{
            padding: '10px',
            backgroundColor: 'white',
            border: 'none',
            borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          title='Configure map legend'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <rect
              x='3'
              y='3'
              width='7'
              height='7'
            />
            <rect
              x='14'
              y='3'
              width='7'
              height='7'
            />
            <rect
              x='14'
              y='14'
              width='7'
              height='7'
            />
            <rect
              x='3'
              y='14'
              width='7'
              height='7'
            />
          </svg>
        </button>

        {/* Recenter Button */}
        <button
          onClick={handleRecenter}
          style={{
            padding: '10px',
            backgroundColor: 'white',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'white';
          }}
          title='Recenter map to show all markers'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <circle
              cx='12'
              cy='12'
              r='10'
            />
            <circle
              cx='12'
              cy='12'
              r='3'
            />
            <line
              x1='12'
              y1='1'
              x2='12'
              y2='5'
            />
            <line
              x1='12'
              y1='19'
              x2='12'
              y2='23'
            />
            <line
              x1='1'
              y1='12'
              x2='5'
              y2='12'
            />
            <line
              x1='19'
              y1='12'
              x2='23'
              y2='12'
            />
          </svg>
        </button>
      </div>

      {/* Map Legend Configuration Dialog */}
      {showLegendConfig && (
        <MapLegendConfig
          onClose={handleLegendClose}
          position={legendPosition}
        />
      )}

      {/* Full Screen Modal Overlay */}
      {selectedMarker && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '20px'
            }}
            onClick={handleCloseInfoWindow}
          >
            {selectedMarker.type === 'search_result' ||
            selectedMarker.type === 'ai_result' ? (
              /* Add Component Modal - Smaller Size */
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  width: '100%',
                  maxWidth: '400px',
                  maxHeight: '500px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    flexShrink: 0
                  }}
                >
                  <p
                    style={{ fontSize: '1.2em', fontWeight: 'bold', margin: 0 }}
                  >
                    {selectedMarker.name}
                  </p>
                  <button
                    onClick={handleCloseInfoWindow}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#842234',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                    title='Close'
                  >
                    ×
                  </button>
                </div>

                {/* Content - scrollable */}
                <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      borderRadius: '6px'
                    }}
                  >
                    {/* Address */}
                    {selectedMarker.notes && selectedMarker.notes[0] && (
                      <div>
                        {(() => {
                          const fullAddress = selectedMarker.notes[0];
                          const parts = fullAddress
                            .split(',')
                            .map((p) => p.trim());
                          const streetAddress = parts[0];
                          const restOfAddress = parts.slice(1).join(', ');

                          return (
                            <>
                              <p
                                style={{
                                  fontSize: '14px',
                                  color: '#000',
                                  margin: 0,
                                  lineHeight: 1.5
                                }}
                              >
                                {streetAddress}
                              </p>
                              {restOfAddress && (
                                <p
                                  style={{
                                    fontSize: '14px',
                                    color: '#000',
                                    margin: 0,
                                    lineHeight: 1.5
                                  }}
                                >
                                  {restOfAddress}
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Website Link */}
                    {selectedMarker.websiteUrl && (
                      <div>
                        <a
                          href={selectedMarker.websiteUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{
                            color: '#1a73e8',
                            textDecoration: 'none',
                            fontSize: '14px'
                          }}
                        >
                          Visit Website
                        </a>
                      </div>
                    )}

                    {/* Add Button */}
                    <button
                      onClick={() => handleAddSearchResult(selectedMarker)}
                      disabled={isAddingActivity}
                      style={{
                        marginTop: '12px',
                        padding: '12px 20px',
                        backgroundColor: '#842234',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '1em',
                        fontWeight: '600',
                        cursor: isAddingActivity ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '2px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (!isAddingActivity) {
                          e.currentTarget.style.backgroundColor = '#6d1829';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAddingActivity) {
                          e.currentTarget.style.backgroundColor = '#842234';
                        }
                      }}
                    >
                      <Plus size={20} />
                      {isAddingActivity ? 'Adding...' : 'Add to Trip'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* CustomInfoWindow Modal - Original Size */
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  width: '100%',
                  height: '100%',
                  maxWidth: '450px',
                  maxHeight: '860px',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  style={{
                    padding: '16px 20px',
                    borderBottom: '1px solid #e5e7eb',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    flexShrink: 0
                  }}
                >
                  <p
                    style={{ fontSize: '1.2em', fontWeight: 'bold', margin: 0 }}
                  >
                    {selectedMarker.name}
                  </p>
                  <button
                    onClick={handleCloseInfoWindow}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#842234',
                      padding: '0 4px',
                      lineHeight: 1
                    }}
                    title='Close'
                  >
                    ×
                  </button>
                </div>

                {/* Content - scrollable */}
                <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
                  {(() => {
                    // Find the full activity object from trip context
                    const fullActivity =
                      selectedMarker.id && trip?.activities
                        ? trip.activities.find(
                            (a: ITripActivity) => a.id === selectedMarker.id
                          )
                        : undefined;

                    return (
                      <CustomInfoWindow
                        id={selectedMarker.id || ''}
                        tripId={selectedMarker.tripId || ''}
                        folders={selectedMarker.folders || []}
                        type={selectedMarker.type}
                        name={selectedMarker.name}
                        notes={selectedMarker.notes}
                        placesId={selectedMarker.placesId}
                        latitude={selectedMarker.lat}
                        longitude={selectedMarker.lng}
                        websiteUrl={selectedMarker.websiteUrl}
                        photos={[]}
                        activity={fullActivity}
                        onSave={handleCustomizationSave}
                        onPreviewChange={handleCustomizationPreview}
                      />
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const MapWithMarkersWrapper: React.FC<MapWithMarkersProps> = (props) => {
  return (
    <APIProvider
      apiKey={config.GOOGLE_MAPS_API_KEY || ''}
      libraries={['places', 'marker']}
    >
      <MapWithMarkers {...props} />
    </APIProvider>
  );
};

export default MapWithMarkersWrapper;
